import { useRuntimeConfig } from '#app';
import { computed } from 'vue';

/**
 * Version information structure for the canvas build.
 */
export interface VersionInfo {
    /** Semantic version from package.json (e.g., "1.13.5") */
    version: string;
    /** Git branch name (e.g., "main", "feature/xyz") */
    branch: string;
    /** Full git commit hash */
    commitHash: string;
    /** Short git commit hash (first 7 characters) */
    commitShortHash: string;
    /** Date of the git commit */
    commitDate: string;
    /** ISO timestamp of when the build was created */
    buildDate: string;
    /** Environment: "dev", "prod", or "staging" */
    environment: string;
    /** Combined version string: "branch-version" */
    fullVersionString: string;
}

/**
 * Composable for accessing canvas version information.
 *
 * @example
 * ```ts
 * const { versionInfo, logVersionInfo } = useVersionInfo();
 *
 * // Access version data
 * console.log(versionInfo.value.version); // "1.13.5"
 *
 * // Log styled version info to console
 * logVersionInfo();
 * ```
 */
export const useVersionInfo = () => {
    const config = useRuntimeConfig();

    const versionInfo = computed<VersionInfo>(() => {
        const { ddVersion, ddEnv, commitHash, commitDate, buildDate } = config.public;

        // ddVersion format is "branch-version" (e.g., "main-1.13.5")
        const versionString = ddVersion || 'unknown-0.0.0';
        const parts = versionString.split('-');
        const version = parts.pop() || 'unknown';
        const branch = parts.join('-') || 'unknown';

        const shortHash = commitHash ? String(commitHash).substring(0, 7) : 'unknown';

        return {
            version,
            branch,
            commitHash: commitHash || 'unknown',
            commitShortHash: shortHash,
            commitDate: commitDate || 'unknown',
            buildDate: buildDate || 'unknown',
            environment: ddEnv || 'unknown',
            fullVersionString: versionString,
        };
    });

    const logVersionInfo = () => {
        const info = versionInfo.value;
        console.group('%c🎯 Canvas Version Info', 'color: #ffd700; font-weight: bold; font-size: 14px;');
        console.log('%cVersion:', 'color: #888; font-weight: bold;', info.version);
        console.log('%cBranch:', 'color: #888; font-weight: bold;', info.branch);
        console.log('%cCommit:', 'color: #888; font-weight: bold;', `${info.commitShortHash} (${info.commitDate})`);
        console.log('%cEnvironment:', 'color: #888; font-weight: bold;', info.environment);
        console.log('%cBuild Date:', 'color: #888; font-weight: bold;', info.buildDate);
        console.log('%cFull String:', 'color: #888; font-weight: bold;', info.fullVersionString);
        console.groupEnd();
        return info;
    };

    return {
        versionInfo,
        logVersionInfo,
    };
};
